import axios from "axios";
import { App } from "@capacitor/app";

import { aiAssistantUrl } from "config/servicesConfig/internalApi";
import { getTodaysforestInternalApiKey } from "contexts/FirebaseContext";
import { getInternalAppType } from "utils/brandingUtils";
import { isNative } from "lib/capacitor/capacitor";
import { InternalAppType } from "types/branding";


const aiAssistantChatUrl = aiAssistantUrl + "/chat?";
const aiAssistantStreamUrl = aiAssistantUrl + "/chat/stream?";
const aiAssistantThreadUrl = aiAssistantUrl + "/chat/thread?";

// Key management for AI assistant API key
// we store the key and its expiration date in sessionStorage to 
// avoid unnecessary calls to get new keys, since generating a 
// new key involves a call to Firebase which can be slow. 
const AI_ASSISTANT_API_KEY_SESSION_KEY = "aiAssistantApiKey";
const AI_ASSISTANT_API_KEY_EXPIRES_AT_KEY = "aiAssistantApiKeyExpiresAt";
const API_KEY_TTL_MS = 60 * 60 * 1000; // 1 hour time to live


// --- AI-Assistant --- //
export type AIChatMessage = {
  userId: string;
  threadId: string;
  message: string;
  context: Record<string, unknown>;
};

export type AIChatThreadRequest = {
  userId: string;
  threadId: string;
  includeAudit?: boolean;
};

export type AIChatThreadPart = {
  text?: string;
  [key: string]: unknown;
};

export type AIChatThreadMessage = {
  id?: string;
  role: "user" | "model" | "tool" | string;
  content: AIChatThreadPart[];
  index: number;
  system?: string;
  savedAt?: string;
};

export type AIChatThreadResponse = {
  messages: AIChatThreadMessage[];
  audit?: AIChatThreadMessage[];
};

type AIStreamEvent =
  | { type: "token"; text: string }
  | { type: "done"; reply: string }
  | { type: "error"; message: string };

const nativeAppBrands: Readonly<Record<string, InternalAppType | undefined>> = {
  "io.georgemaps.app": "george",
  "com.forsler.app": "forsler",
  "com.forsler.skovkortet": "skovkortet",
};

async function withAssistantIdentity(context: AIChatMessage["context"]) {
  let internalAppType = getInternalAppType({ pathOrHost: window.location.hostname });
  // Native identity must come from the build, never an account or WebView hostname.
  if (isNative && !["george", "skovkortet", "forsler"].includes(process.env.REACT_APP_INTERNAL_APP_TYPE ?? "")) {
    let appId: string;
    try {
      appId = (await App.getInfo()).id;
    } catch {
      console.warn("Cannot resolve native assistant identity: build brand is missing or invalid and app info is unavailable");
      throw new Error("Unable to resolve native assistant app identity");
    }
    const nativeBrand = Object.prototype.hasOwnProperty.call(nativeAppBrands, appId)
      ? nativeAppBrands[appId]
      : undefined;
    if (!nativeBrand) {
      console.warn("Cannot resolve native assistant identity: unrecognized bundle ID", appId);
      throw new Error("Unable to resolve native assistant app identity");
    }
    console.warn("Native assistant build brand is missing or invalid; using bundle ID", appId);
    internalAppType = nativeBrand;
  }
  const app = context.app;
  return {
    ...context,
    app: {
      ...(app && typeof app === "object" ? app : {}),
      // Resolve at the request boundary so stale or early context cannot choose the brand.
      internalAppType,
    },
  };
}


export async function aiAssistantChat({
  userId,
  threadId,
  message,
  context,
}: AIChatMessage) {
  const apiKey = await getSessionAiAssistantApiKey();
  const res = await axios.post(`${aiAssistantChatUrl}key=${apiKey}`, {
    uid: userId,
    thread: threadId,
    prompt: message,
    context: await withAssistantIdentity(context),
  });
  return res.data;
}

export async function aiAssistantChatStream(
  { userId, threadId, message, context }: AIChatMessage,
  {
    signal,
    onToken,
    onDone,
    onError,
  }: {
    signal?: AbortSignal;
    onToken?: (text: string) => void;
    onDone?: (reply: string) => void;
    onError?: (message: string) => void;
  } = {},
) {
  const apiKey = await getSessionAiAssistantApiKey();;
  const response = await fetch(`${aiAssistantStreamUrl}key=${apiKey}`, {
    method: "POST",
    headers: {
      Accept: "text/event-stream",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      uid: userId,
      thread: threadId,
      prompt: message,
      context: await withAssistantIdentity(context),
    }),
    signal,
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`AI assistant stream failed: ${response.status} ${text}`);
  }

  if (!response.body) {
    throw new Error("AI assistant stream has no body");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let finalReply = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const events = buffer.split("\n\n");
    buffer = events.pop() ?? "";

    for (const event of events) {
      const trimmed = event.trim();
      if (!trimmed || trimmed.startsWith(":")) continue;
      const dataLines = trimmed
        .split("\n")
        .filter((line) => line.startsWith("data:"))
        .map((line) => line.slice(5).trim());
      if (dataLines.length === 0) continue;
      const raw = dataLines.join("\n");
      let parsed: AIStreamEvent;
      try {
        parsed = JSON.parse(raw) as AIStreamEvent;
      } catch {
        continue;
      }
      if (parsed.type === "token") {
        finalReply += parsed.text;
        onToken?.(parsed.text);
      } else if (parsed.type === "done") {
        finalReply = parsed.reply;
        onDone?.(parsed.reply);
        return parsed.reply;
      } else if (parsed.type === "error") {
        onError?.(parsed.message);
        throw new Error(parsed.message);
      }
    }
  }

  return finalReply;
}

export async function aiAssistantChatThread({
  userId,
  threadId,
  includeAudit = false,
}: AIChatThreadRequest): Promise<AIChatThreadResponse> {
  const apiKey = await getSessionAiAssistantApiKey();
  const res = await axios.post(`${aiAssistantThreadUrl}key=${apiKey}`, {
    uid: userId,
    thread: threadId,
    includeAudit,
  });
  return res.data;
}

// --- API key management --- //
async function getSessionAiAssistantApiKey() {
  const cachedKey = sessionStorage.getItem(AI_ASSISTANT_API_KEY_SESSION_KEY);
  const expiresAt = Number(sessionStorage.getItem(AI_ASSISTANT_API_KEY_EXPIRES_AT_KEY));

  if (cachedKey && expiresAt && Date.now() < expiresAt) {
    return cachedKey;
  }

  const apiKey = await getTodaysforestInternalApiKey();
  if (!apiKey) throw new Error("missing credentials");

  sessionStorage.setItem(AI_ASSISTANT_API_KEY_SESSION_KEY, apiKey);
  sessionStorage.setItem(
    AI_ASSISTANT_API_KEY_EXPIRES_AT_KEY,
    String(Date.now() + API_KEY_TTL_MS),
  );

  return apiKey;
}

export function clearSessionAiAssistantApiKey() {
  sessionStorage.removeItem(AI_ASSISTANT_API_KEY_SESSION_KEY);
  sessionStorage.removeItem(AI_ASSISTANT_API_KEY_EXPIRES_AT_KEY);
}
